import React, { useState } from 'react';
import { Button, Switch, Typography, showToast } from '@attentive-platform/stem-ui';
import { fetchApiData } from '../Utils/ApiCalls';
import { ATTACH_PLAN_ENDPOINT, ATTACH_PLAN_INFO_ENDPOINT } from '../urls';
import useFormStatus from '../Hooks/useFormStatus';
import { Alert } from './Alert';
import { isBeamUser } from '../Utils/HelperFunctions';

const ProConversionForm = () => {
  const [email, setEmail] = useState('');
  const [blueprintAccess, setBlueprintAccess] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [attachLoading, setAttachLoading] = useState(false);

  const { successMessage, errorMessage, handleApiResult } = useFormStatus();

  const resetFlow = () => {
    setEmail('');
    setBlueprintAccess(false);
    setPreview(null);
  };

  /** Falcon only: load preview from attach plan info (GET). */
  const handleNext = async e => {
    e.preventDefault();
    if (isBeamUser()) return;

    const addr = email?.trim();
    if (!addr) {
      showToast('Please enter an admin email.', 'error');
      return;
    }

    setPreviewLoading(true);
    try {
      const response = await fetchApiData({
        apiRequest: 'GET',
        endpoint: `${ATTACH_PLAN_INFO_ENDPOINT}?email=${encodeURIComponent(addr.toLowerCase())}`
      });

      if (response.error) {
        showToast(response.error || 'Could not load attach plan preview', 'error');
        setPreview(null);
        return;
      }

      const data = response.data || {};
      setPreview(data);
      const beamRegistered = data?.company?.is_beam_registered === true;
      setBlueprintAccess(beamRegistered ? false : Boolean(data?.company?.pro_blueprint_access));
    } catch {
      showToast('Could not load attach plan preview', 'error');
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  /**
   * Beam: POST ATTACH_PLAN_ENDPOINT (email only).
   * Falcon: POST ATTACH_PLAN_ENDPOINT with blueprint flag (after preview from ATTACH_PLAN_INFO_ENDPOINT).
   */
  const handleAttach = async e => {
    e.preventDefault();
    const addr = email?.trim().toLowerCase();
    if (!addr) {
      showToast('Please enter an admin email.', 'error');
      return;
    }

    if (isBeamUser()) {
      setAttachLoading(true);
      try {
        const response = await fetchApiData({
          endpoint: ATTACH_PLAN_ENDPOINT,
          data: { email: addr }
        });
        handleApiResult(response);
        if (response.error === null) {
          resetFlow();
        }
      } finally {
        setAttachLoading(false);
      }
      return;
    }

    if (!preview?.can_attach_plan) {
      showToast('Plan cannot be attached for this user/company.', 'error');
      return;
    }

    const beamRegistered = preview?.company?.is_beam_registered === true;
    const payload = { email: addr };
    payload.pro_blueprint_access = beamRegistered ? false : Boolean(blueprintAccess);

    setAttachLoading(true);
    try {
      const response = await fetchApiData({
        endpoint: ATTACH_PLAN_ENDPOINT,
        data: payload
      });

      handleApiResult(response);
      if (response.error === null) {
        resetFlow();
      }
    } finally {
      setAttachLoading(false);
    }
  };

  const handleFormSubmit = e => {
    if (isBeamUser()) {
      return handleAttach(e);
    }
    return preview ? handleAttach(e) : handleNext(e);
  };

  const handleBack = () => {
    setPreview(null);
    setBlueprintAccess(false);
  };

  const company = preview?.company;
  const beamRegistered = company?.is_beam_registered === true;
  const canAttach = Boolean(preview?.can_attach_plan);

  return (
    <form onSubmit={handleFormSubmit} className='w-100'>
      <Alert errorMessage={errorMessage} successMessage={successMessage} />
      <div className='input-group mb-3'>
        <input
          type='Email'
          value={email}
          onChange={e => setEmail(e.target.value)}
          disabled={Boolean(preview)}
          required={!preview}
          className='form-control form-control-lg'
          placeholder='Admin email'
          aria-label='Email'
        />
      </div>

      {!preview && isBeamUser() && (
        <div className='button mb-3'>
          <Button color='primary' size='large' variant='contained' type='submit' loading={attachLoading}>
            Add to PRO plan
          </Button>
        </div>
      )}

      {!preview && !isBeamUser() && (
        <div className='button mb-3'>
          <Button color='primary' size='large' variant='contained' type='submit' loading={previewLoading}>
            Next
          </Button>
        </div>
      )}

      {preview && !isBeamUser() && (
        <>
          <Typography variant='subtitle1-medium' className='mb-3 mt-3'>
            Pro Blueprint access
          </Typography>
          <div className='flex ml-4 mb-4'>
            <Switch
              label=''
              size='small'
              checked={blueprintAccess}
              disabled={beamRegistered}
              onChange={e => setBlueprintAccess(e.target.checked)}
            />
            <div className='flex flex-col'>
              <Typography variant='subtitle1-medium'>Enable Blueprint DFY access</Typography>
              <Typography variant='body2' color='var(--color-neutral-50)'>
                When ON, user can switch to Blueprint mode and submit construction DFY at default per-sheet pricing
                (usage-based). When OFF, Blueprint mode is disabled for this company.
              </Typography>
            </div>
          </div>
        </>
      )}

      {preview && !isBeamUser() && (
        <div className='button flex gap-2 flex-wrap'>
          <Button type='button' variant='outlined' onClick={handleBack}>
            Back
          </Button>
          <Button
            color='primary'
            size='large'
            variant='contained'
            type='submit'
            loading={attachLoading}
            disabled={!canAttach}>
            Add to PRO plan
          </Button>
        </div>
      )}
    </form>
  );
};

export default ProConversionForm;
